import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
    AccountDeletionStatusResponse,
    RequestAccountDeletionResponse,
    ConfirmAccountDeletionResponse,
    CancelAccountDeletionResponse,
} from '../types';

export function useAccountDeletionStatus() {
    return useQuery({
        queryKey: ['accountDeletionStatus'],
        queryFn: () => api.get<AccountDeletionStatusResponse>('/api/account/deletion/status'),
    });
}

export function useRequestAccountDeletion() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.post<RequestAccountDeletionResponse>('/api/account/deletion/request'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountDeletionStatus'] });
            queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
        },
    });
}

export function useConfirmAccountDeletion() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (token: string) =>
            api.get<ConfirmAccountDeletionResponse>(
                `/api/account/deletion/confirm?token=${encodeURIComponent(token)}`
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountDeletionStatus'] });
        },
    });
}

export function useCancelAccountDeletion() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.post<CancelAccountDeletionResponse>('/api/account/deletion/cancel'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['accountDeletionStatus'] });
            queryClient.invalidateQueries({ queryKey: ['accountProfile'] });
        },
    });
}

export function useRefreshAccountDeletionStatus() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: ['accountDeletionStatus'] });
    };
}
